/* eslint-disable react-hooks/exhaustive-deps, jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
import { startTransition, useEffect, useId, useRef } from "react"
import type { FC, FocusEvent, KeyboardEvent, MouseEvent, ReactElement, RefObject } from "react"

import { AnimatePresence, m as motion, useReducedMotion } from "framer-motion"
import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"
import { createPortal } from "react-dom"
import { useBoolean, useClickAway, useEvent, useKeyPressEvent } from "react-use"

import { ETypographySize, Typography } from "@/3514/components"
import { withPressHandler } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

import classes from "./session-info.styles"
import { type TSelectInfoDropdownItem as TItem } from "./session-info.types"

type TSessionInfoDropdownProps = {
    actions: TItem[]
}
const SessionInfoDropdown: FC<TSessionInfoDropdownProps> = ({ actions }: TSessionInfoDropdownProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const uniqueId: string = useId()

    const isMotionReduced: boolean = useReducedMotion()

    const openState: ReturnType<typeof useBoolean> = useBoolean(false)

    const containerRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)
    const listRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)
    const firstItemRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    useEffect((): void => {
        if (openState[0]) firstItemRef.current?.focus()
    }, [openState[0]])

    useClickAway(containerRef, (): void => startTransition((): void => openState[1](false)))

    useKeyPressEvent("Escape", (): void => startTransition((): void => openState[1](false)))

    useEvent("click", function (e: Event): void {
        if (!openState[0]) return

        const target: Node = e.target as Node

        if (containerRef.current?.contains(target) || listRef.current?.contains(target)) return

        startTransition((): void => openState[1](false))
    })

    useEvent("wheel", function (): void {
        if (!openState[0]) return

        startTransition((): void => openState[1](false))
    })

    function handleContainerClick(e: MouseEvent): void {
        e.stopPropagation()
    }

    function handleOpenerAction(): void {
        startTransition((): void => openState[1](!openState[0]))
    }

    function handleOpenerKeyDown(e: KeyboardEvent): void {
        if (e.key !== "Enter") return

        e.stopPropagation()
        handleOpenerAction()
    }

    function handleListBlur(e: FocusEvent<HTMLUListElement>): void {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) startTransition((): void => openState[1](false))
    }

    function handleListKeyDown(e: KeyboardEvent<HTMLUListElement>): void {
        if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return

        e.preventDefault()

        const items: NodeListOf<HTMLElement> = e.currentTarget.querySelectorAll<HTMLElement>("[role='button']")

        const current: number = Array.from(items).indexOf(document.activeElement as HTMLElement)
        const next: number =
            e.key === "ArrowDown" ? (current + 1) % items.length : (current - 1 + items.length) % items.length

        items[next]?.focus()
    }

    function getListPosition(): Partial<DOMRect> {
        const rect: DOMRect | undefined = containerRef.current?.getBoundingClientRect()

        return { top: rect?.bottom ?? 0, left: (rect?.left ?? 0) + (rect?.width ?? 0) / 2 }
    }

    function renderList(): ReactElement {
        const { top, left } = getListPosition()

        return createPortal(
            <AnimatePresence>
                {openState[0] && (
                    <div
                        key={`${uniqueId}-sid-wrapper`}
                        ref={listRef}
                        className={classes.dropdown.listWrapper()}
                        style={{ top, left }}
                    >
                        <motion.div
                            initial={{ opacity: 0, y: -6, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -6, scale: 0.97 }}
                            transition={{ duration: isMotionReduced ? 0 : 0.2, ease: "easeOut" }}
                        >
                            <div className={classes.dropdown.triangleWrapper()}>
                                <div className={classes.dropdown.triangleOuter()} />
                                <div className={classes.dropdown.triangleInner()} />
                            </div>

                            <ul
                                role="menu"
                                className={classes.dropdown.list()}
                                onBlur={handleListBlur}
                                onKeyDown={handleListKeyDown}
                            >
                                {map<TItem, ReactElement>(actions, (item: TItem, idx: number): ReactElement => {
                                    const actionHandler: ReturnType<typeof withPressHandler> = withPressHandler(
                                        async (): Promise<void> => {
                                            startTransition((): void => openState[1](false))

                                            await item.actionHandler()
                                        }
                                    )

                                    return (
                                        <li
                                            key={`sid-item-${uniqueId}-${idx}`}
                                            className={classes.dropdown.listItem({
                                                isLast: idx === actions.length - 1
                                            })}
                                            role="menuitem"
                                        >
                                            <div
                                                ref={idx === 0 ? firstItemRef : undefined}
                                                role="button"
                                                tabIndex={0}
                                                aria-label={item.ariaLabel}
                                                onMouseEnter={item.focusHandler}
                                                onFocus={item.focusHandler}
                                                onClick={actionHandler}
                                                onKeyDown={actionHandler}
                                                className={classes.dropdown.listItemInner()}
                                            >
                                                <Typography text={item.label} size={ETypographySize.Small} />
                                            </div>
                                        </li>
                                    )
                                })}
                            </ul>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>,
            document.body
        )
    }

    return isEmpty(actions) ? null : (
        <div ref={containerRef} className={classes.dropdown.container()} onClick={handleContainerClick}>
            <button
                type="button"
                aria-label={t("entities.sessions.dropdown.opener.ariaLabel")}
                aria-expanded={openState[0]}
                aria-haspopup="menu"
                tabIndex={0}
                onKeyDown={handleOpenerKeyDown}
                onClick={handleOpenerAction}
                className={classes.dropdown.opener()}
            >
                <div className={classes.dropdown.dots()}>
                    {Array.from(
                        { length: 3 },
                        (_: unknown, index: number): ReactElement => (
                            <i key={`sid-dot-${uniqueId}-${index}`} className={classes.dropdown.dot()} />
                        )
                    )}
                </div>
            </button>

            {renderList()}
        </div>
    )
}

SessionInfoDropdown.displayName = "SessionInfoDropdown"

export default SessionInfoDropdown
